import { useState } from "react";
import { useFilteredRequisitions } from "../hooks/useRequisition";
import { RequisitionsTable } from "../components/requisition/RequisitionsTable";
import { RequisitionFilters } from "../components/requisition/RequisitionFilters";
import { useDebounce } from "@/shared/hooks/useDebounce";

type FilterType = "all" | "my" | "pending-approvals" | "pending-issue";

interface TabState {
  pageNumber: number;
  pageSize: number;
  searchTerm: string;
  searchField: string;
  sortBy: string;
  sortDescending: boolean;
}

const DEFAULT_TAB_STATE: TabState = {
  pageNumber: 1,
  pageSize: 20,
  searchTerm: "",
  searchField: "all",
  sortBy: "requisitionDate",
  sortDescending: true,
};

const createInitialTabState = (): Record<FilterType, TabState> => ({
  all: { ...DEFAULT_TAB_STATE },
  my: { ...DEFAULT_TAB_STATE },
  "pending-approvals": { ...DEFAULT_TAB_STATE },
  "pending-issue": { ...DEFAULT_TAB_STATE },
});

export const RequisitionsPage = () => {
  const [filterType, setFilterType] = useState<FilterType>("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [greenhouseId, setGreenhouseId] = useState("");
  const [tabState, setTabState] = useState<Record<FilterType, TabState>>(createInitialTabState);
  const activeState = tabState[filterType];
  const debouncedSearchTerm = useDebounce(activeState.searchTerm, 400);

  const updateActiveTab = (patch: Partial<TabState>) => {
    setTabState((prev) => ({
      ...prev,
      [filterType]: {
        ...prev[filterType],
        ...patch,
      },
    }));
  };

  const resetActivePage = () => updateActiveTab({ pageNumber: 1 });

  const { data: requisitionsData, isLoading, error, refetch } = useFilteredRequisitions(
    filterType,
    {
      pageNumber: activeState.pageNumber,
      pageSize: activeState.pageSize,
      searchTerm: debouncedSearchTerm.trim() || undefined,
      searchField: activeState.searchField,
      sortBy: activeState.sortBy,
      sortDescending: activeState.sortDescending,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      greenhouseId: greenhouseId || undefined,
    },
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Requisitions</h1>
        <p className="text-gray-500 mt-1">
          View and manage stock requisitions
        </p>
      </div>

      <RequisitionFilters
        filterType={filterType}
        onFilterTypeChange={setFilterType}
        startDate={startDate}
        onStartDateChange={(value) => {
          setStartDate(value);
          resetActivePage();
        }}
        endDate={endDate}
        onEndDateChange={(value) => {
          setEndDate(value);
          resetActivePage();
        }}
        greenhouseId={greenhouseId}
        onGreenhouseIdChange={(value) => {
          setGreenhouseId(value);
          resetActivePage();
        }}
      />

      <RequisitionsTable
        requisitions={requisitionsData?.items ?? []}
        onRequisitionUpdated={refetch}
        searchTerm={activeState.searchTerm}
        onSearchTermChange={(term) => updateActiveTab({ searchTerm: term, pageNumber: 1 })}
        currentPage={requisitionsData?.currentPage ?? activeState.pageNumber}
        pageSize={requisitionsData?.pageSize ?? activeState.pageSize}
        totalCount={requisitionsData?.totalCount ?? 0}
        totalPages={requisitionsData?.totalPages ?? 1}
        onPageChange={(page) => updateActiveTab({ pageNumber: page })}
        onPageSizeChange={(size) => updateActiveTab({ pageSize: size, pageNumber: 1 })}
        isLoading={isLoading}
        error={error}
      />
    </div>
  );
};
